import DataTable from "react-data-table-component";
import DateRangePicker from "../../../components/DateRangePicker";
import { Link, useLocation } from "react-router-dom";
import { format } from "date-fns";
import { customStyles } from "../../../assets/global/table-style";
import { useState, useRef, useEffect, useCallback } from "react";
import { useServerPagination } from "../../../hooks/customHook/paginationHook/useServerPagination";
import BASE_URL from "../../../assets/global/url";
import { PaginationControls } from "../../../hooks/customHook/paginationHook/usePagination";
import swal from "sweetalert";
import { formatCurrency } from "../../../utils/numberFormatter";

const PayableTransactionList = () => {
  const location = useLocation();
  const passedTransaction = location.state?.transactions ?? [];
  const passedVendor = location.state?.vendor ?? null;
  const passedCutOff = location.state?.selectedCutOff ?? {
    from: null,
    to: null,
  };

  // use States
  const [filterColumn, setFilterColumn] = useState("all");
  const [currentCutOff, setCurrentCutOff] = useState(passedCutOff);
  const [searchTextTransaction, setSearchTextTransaction] = useState("");

  const debounceTimer = useRef(null);

  const selectedTransactionList = useServerPagination(
    `${BASE_URL}/payable/fetchSelectedTransactions`,
    10,
    {
      transactionIds: passedTransaction.join(","),
      startDate: passedCutOff?.from,
      endDate: passedCutOff?.to,
      searchText: "",
      filterColumn: "all",
    },
  );

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const handleDateRangeChange = useCallback(
    (start, end) => {
      setCurrentCutOff({ from: start, to: end });
      selectedTransactionList.updateParams({
        startDate: start,
        endDate: end,
      });
    },
    [selectedTransactionList],
  );

  const handleSearchTransaction = useCallback(
    (value) => {
      setSearchTextTransaction(value);

      // Clear existing timer to prevent race conditions
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        selectedTransactionList.updateParams({
          searchText: value,
          filterColumn: filterColumn,
        });
      }, 500);
    },
    [filterColumn, selectedTransactionList],
  );

  const handleFilterColumnChange = useCallback(
    (column) => {
      setFilterColumn(column);

      selectedTransactionList.updateParams({
        filterColumn: column,
        searchText: searchTextTransaction,
      });
    },
    [searchTextTransaction, selectedTransactionList],
  );

  const handleSortTransaction = useCallback(
    (column, sortDirection) => {
      selectedTransactionList.updateParams({
        sortColumn: column.sortField,
        sortDirection: sortDirection,
      });
    },
    [selectedTransactionList],
  );

  const columnTransactionList = [
    {
      name: "Transaction Date",
      sortable: true,
      sortField: "createdAt",
      selector: (row) =>
        row?.createdAt
          ? format(new Date(row.createdAt), "MMM/dd/yyyy, hh:mm a")
          : "---",
    },
    {
      name: "Transaction No.",
      sortable: true,
      sortField: "client_transaction_id",
      selector: (row) =>
        row?.client_transaction_id ? row.client_transaction_id : "---",
    },
    {
      name: "Discount",
      sortable: true,
      sortField: "discount_value",
      selector: (row) => (row?.discount_value ? row.discount_value : "---"),
    },
    {
      name: "Destination",
      sortable: true,
      sortField: "domestic_type",
      selector: (row) =>
        row?.domestic_type === "local"
          ? "Local"
          : row?.domestic_type
            ? "Overseas"
            : "---",
    },
    {
      name: "Deadline",
      sortable: true,
      sortField: "due_date",
      selector: (row) =>
        row?.due_date ? format(new Date(row?.due_date), "MMM/dd/yyyy") : "---",
    },
    {
      name: "Purchase Date",
      sortable: true,
      sortField: "purchaseDate",
      selector: (row) =>
        row?.purchaseDate
          ? format(new Date(row?.purchaseDate), "MMM/dd/yyyy")
          : "---",
    },
    {
      name: "Date Approved",
      sortable: true,
      sortField: "date_approved",
      selector: (row) =>
        row?.date_approved
          ? format(new Date(row?.date_approved), "MMM/dd/yyyy, hh:mm a")
          : "---",
    },
    {
      name: "Total Amount",
      sortable: true,
      sortField: "totalPrice",
      selector: (row) => (
        <div className="text-center fw-bold">
          {formatCurrency(row?.totalPrice, row?.currency?.currency_name)}
        </div>
      ),
    },
    {
      name: "Re-Open Amount",
      selector: (row) => row.re_open_amount,
      cell: (row) => {
        const amount = Number(row.re_open_amount);

        const textColor =
          row.re_open_amount == null
            ? "inherit"
            : amount < 0
              ? "#dc3545"
              : "#198754";

        return (
          <div
            style={{ position: "relative", paddingTop: "14px", width: "100%" }}
          >
            <div style={{ fontWeight: 500, color: textColor }}>
              {row.re_open_amount != null
                ? formatCurrency(amount, row?.currency?.currency_name)
                : "---"}
            </div>
          </div>
        );
      },
    },
    {
      name: "Status",
      sortable: true,
      sortField: "status",
      selector: (row) => (
        <div
          className="text-center fw-bold"
          style={{
            color:
              row?.status === "Paid"
                ? "blue"
                : row?.status === "Approved"
                  ? "green"
                  : "orange",
          }}
        >
          {row?.status}
        </div>
      ),
    },
    {
      name: "Action",
      cell: (row) => {
        const isReOpened = Number(row.re_open_amount) > 0;

        const handleReOpenAlert = () => {
          swal({
            title: "Already Re-Opened",
            text: "This transaction has already been re-opened. Please go to the Purchase Management Pending tab to view the re-open transaction.",
            icon: "warning",
            button: "OK",
          });
        };

        const actionButton = (
          <button
            title={isReOpened ? "Already Re-Opened" : "Edit"}
            onClick={isReOpened ? handleReOpenAlert : undefined}
            style={{
              background: "none",
              border: "1px solid #ccc",
              borderRadius: "6px",
              padding: "5px 8px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#555",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#2D2C67";
              e.currentTarget.style.color = "#2D2C67";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#ccc";
              e.currentTarget.style.color = "#555";
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
        );

        return (
          <div>
            {isReOpened ? (
              actionButton
            ) : (
              <Link
                to={`/purchases/re-open-tickets/${row.id}?page=${1}`}
                state={{
                  transactions: passedTransaction,
                  vendor: passedVendor,
                  selectedCutOff: passedCutOff,
                }}
              >
                {actionButton}
              </Link>
            )}
          </div>
        );
      },
      ignoreRowClick: true,
      width: "80px",
      center: true,
    },
  ];

  return (
    <div className="h-100 w-100 custom-container bg-white">
      <div className="w-100 p-2 d-flex flex-row justify-content-between">
        <div className="d-flex flex-column title-custom">
          <span className="fs-3">
            {" "}
            <Link to="/purchases/payable" className="text-dark mx-2">
              <i className="fa-solid fa-arrow-left"></i>
            </Link>
            TRANSACTION LIST | {passedVendor?.company_name}
          </span>
        </div>
      </div>

      <div className="d-flex flex-wrap justify-content-between align-items-end mb-3 gap-2">
        <div style={{ minWidth: "250px" }}>
          <DateRangePicker
            // defaultDateRange={{
            //   start: currentCutOff?.from,
            //   end: currentCutOff?.to,
            // }}
            startDate={currentCutOff?.from}
            endDate={currentCutOff?.to}
            onDateRangeChange={handleDateRangeChange}
            label="Transaction Date"
          />
        </div>

        <div className="input-group" style={{ maxWidth: "400px" }}>
          <input
            type="text"
            className="form-control"
            placeholder="Search"
            value={searchTextTransaction}
            onChange={(e) => handleSearchTransaction(e.target.value)}
          />
          <button
            type="button"
            className="btn btn-outline-secondary dropdown-toggle dropdown-toggle-split"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="fa-solid fa-sliders"></i>
            <span className="visually-hidden">Toggle Dropdown</span>
          </button>
          <ul className="dropdown-menu dropdown-menu-end">
            <li>
              <button
                className={`dropdown-item ${
                  filterColumn === "all" ? "active" : ""
                }`}
                onClick={() => handleFilterColumnChange("all")}
              >
                All
              </button>
            </li>
            <li>
              <button
                className={`dropdown-item ${
                  filterColumn === "createdAt" ? "active" : ""
                }`}
                onClick={() => handleFilterColumnChange("createdAt")}
              >
                Transaction Date
              </button>
            </li>
            <li>
              <button
                className={`dropdown-item ${
                  filterColumn === "client_transaction_id" ? "active" : ""
                }`}
                onClick={() =>
                  handleFilterColumnChange("client_transaction_id")
                }
              >
                Transaction No.
              </button>
            </li>
            <li>
              <button
                className={`dropdown-item ${
                  filterColumn === "product_name" ? "active" : ""
                }`}
                onClick={() => handleFilterColumnChange("product_name")}
              >
                Product Name
              </button>
            </li>
            <li>
              <button
                className={`dropdown-item ${
                  filterColumn === "product_code" ? "active" : ""
                }`}
                onClick={() => handleFilterColumnChange("product_code")}
              >
                Product Code
              </button>
            </li>
            <li>
              <button
                className={`dropdown-item ${
                  filterColumn === "discount_value" ? "active" : ""
                }`}
                onClick={() => handleFilterColumnChange("discount_value")}
              >
                Discount
              </button>
            </li>
            <li>
              <button
                className={`dropdown-item ${
                  filterColumn === "domestic_type" ? "active" : ""
                }`}
                onClick={() => handleFilterColumnChange("domestic_type")}
              >
                Destination
              </button>
            </li>
            <li>
              <button
                className={`dropdown-item ${
                  filterColumn === "due_date" ? "active" : ""
                }`}
                onClick={() => handleFilterColumnChange("due_date")}
              >
                Deadline
              </button>
            </li>
            <li>
              <button
                className={`dropdown-item ${
                  filterColumn === "purchaseDate" ? "active" : ""
                }`}
                onClick={() => handleFilterColumnChange("purchaseDate")}
              >
                Purchase Date
              </button>
            </li>
            <li>
              <button
                className={`dropdown-item ${
                  filterColumn === "date_approved" ? "active" : ""
                }`}
                onClick={() => handleFilterColumnChange("date_approved")}
              >
                Date Approved
              </button>
            </li>
            <li>
              <button
                className={`dropdown-item ${
                  filterColumn === "totalPrice" ? "active" : ""
                }`}
                onClick={() => handleFilterColumnChange("totalPrice")}
              >
                Sub Total
              </button>
            </li>
            <li>
              <button
                className={`dropdown-item ${
                  filterColumn === "status" ? "active" : ""
                }`}
                onClick={() => handleFilterColumnChange("status")}
              >
                Status
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div className="data-table-cell-width">
        <DataTable
          columns={columnTransactionList}
          data={selectedTransactionList.data}
          customStyles={customStyles}
          className="dataTable"
          highlightOnHover
          sortServer={true}
          onSort={handleSortTransaction}
        />
        <PaginationControls {...selectedTransactionList} />
      </div>
    </div>
  );
};

export default PayableTransactionList;
